// Ошибки загрузки списков и карточек (README «Состояния и движение»: ошибка — сообщением с кодом,
// во время загрузки — её признак; ux.md, раздел 14; Constitution V: причина, следующий шаг, код
// и номер операции). «Повторить» — тот же запрос; пока он идёт, на кнопке спиннер на месте текста
// той же ширины, повторное нажатие не срабатывает (T164). Ошибка остаётся на экране во время
// повторного запроса, когда данные уже были (не удалось обновить список или панель): без признака
// загрузки нажатие выглядит несработавшим. Данных не было — запрос снова «загружается», и вместо
// ошибки сразу встаёт загрузка списка (скелет после 0,3 с).
import { Button, Callout, EmptyState } from '@kp/ui';
import { useState } from 'react';
import { errorView } from '../app/errors.ts';
import type { ErrorView } from '../app/errors.ts';

/**
 * Список или карточка не загрузились: «Не удалось загрузить», причина и шаг, код и номер
 * операции, «Повторить». Без onRetry кнопки нет: «Повторить» одна на экран (README «Цвет») или
 * тот же запрос получит тот же отказ.
 */
export function LoadErrorState({
  view,
  onRetry,
  retrying = false,
  secondary = false,
  title = 'Не удалось загрузить',
  text = `${view.message}. ${view.nextStep}`,
  headingLevel,
}: {
  view: ErrorView;
  onRetry?: () => void;
  /** Повторный запрос идёт (isFetching запроса): «Повторить» со спиннером. */
  retrying?: boolean;
  /**
   * «Повторить» — вторичная: слой поверх списка со своей кнопкой-акцентом (боковая панель
   * записи) — акцент на экране один (README «Цвет»).
   */
  secondary?: boolean;
  title?: string;
  text?: string;
  /** Уровень заголовка, когда ошибка — часть карточки или окна: секция панели — 4, окно — 3. */
  headingLevel?: 2 | 3 | 4;
}) {
  return (
    <EmptyState
      error
      title={title}
      text={text}
      headingLevel={headingLevel}
      code={view.code}
      operation={view.operation}
      action={
        onRetry ? (
          <Button
            variant={secondary ? 'secondary' : 'primary'}
            loading={retrying}
            onClick={onRetry}
          >
            Повторить
          </Button>
        ) : undefined
      }
    />
  );
}

/** Запрос TanStack Query, сбой которого показывают с «Повторить». */
export interface RetryableQuery {
  isError: boolean;
  error: unknown;
  data: unknown;
  /** Данные прежнего запроса (`keepPreviousData`) на время нового — не его собственные. */
  isPlaceholderData?: boolean;
  fetchStatus: 'fetching' | 'paused' | 'idle';
  refetch: () => Promise<unknown>;
}

export interface QueryFailure {
  /** Причина, код и номер операции, пока показать нечего: сбой — и его повтор до ответа. */
  failure: ErrorView | null;
  /** «Повторить» нажата, ответа ещё нет: ошибка на месте, на «Повторить» — спиннер. */
  retrying: boolean;
  /** Тот же запрос; пока он идёт, повторное нажатие второго не даёт. */
  retry: () => void;
}

/**
 * Сбой запроса без данных и его повтор (T135, T168; README «Состояния и движение»). TanStack
 * Query v5 на повторе запроса без данных возвращает его в «pending» и стирает ошибку: выноска
 * пропала бы до ответа, а место под полем схлопнулось. Пока повтор идёт, ошибка остаётся с той же
 * причиной и спиннером на «Повторить» — раскладка не прыгает; ответ пришёл — данные или новая
 * ошибка. show = false — сбой не показывается (поле не спрашивало, отказ в доступе).
 */
export function useQueryFailure(
  query: RetryableQuery,
  { show = true }: { show?: boolean } = {},
): QueryFailure {
  const [retried, setRetried] = useState<ErrorView | null>(null);
  // С keepPreviousData на повторе приходят варианты прежнего запроса (другой строки поиска): это
  // не ответ на повтор — выноска остаётся до него, а прежние варианты не показываются.
  const ownData = query.data !== undefined && query.isPlaceholderData !== true;
  const current = show && query.isError && !ownData ? errorView(query.error) : null;
  const retrying =
    show && current === null && retried !== null && !ownData && query.fetchStatus !== 'idle';
  return {
    failure: current ?? (retrying ? retried : null),
    retrying,
    retry: () => {
      if (retrying || current === null) return;
      setRetried(current);
      void query.refetch().finally(() => setRetried(null));
    },
  };
}

/**
 * Список для выбора не загрузился (T135): выноска под полем и «Повторить» — тот же запрос.
 * Выноска — только пока вариантов нет совсем; на время повтора она остаётся на месте со спиннером
 * на «Повторить» (useQueryFailure, T168), поле показывает свою загрузку. Пустым списком («Никого
 * не нашлось») ошибка не показывается.
 */
export function ListLoadError({
  what,
  view,
  onRetry,
  retrying = false,
}: {
  /** Что не загрузилось: «Не удалось загрузить список пользователей». */
  what: string;
  view: ErrorView;
  onRetry: () => void;
  /** Повтор идёт: спиннер на месте текста той же ширины, повторное нажатие не срабатывает. */
  retrying?: boolean;
}) {
  return (
    <Callout
      tone="bad"
      role="alert"
      code={view.code}
      operation={view.operation}
      action={
        <Button size="sm" loading={retrying} onClick={onRetry}>
          Повторить
        </Button>
      }
    >
      {what}. {view.message}. {view.nextStep}
    </Callout>
  );
}

/** Строка в раскрытом списке вместо «Никого не нашлось»: что случилось и код. */
export function failedListText(what: string, view: ErrorView): string {
  return `${what}. Код ошибки ${view.code}`;
}
